async function initHeroBlur(block, mountOverride = null) {
	const mount = mountOverride || (block ? block.querySelector('.fc-hero-blur__canvas') : null);
	if (!mount || mount.dataset.fcHeroBlurInit) {
		return;
	}
	mount.dataset.fcHeroBlurInit = '1';
	// Keep Three.js out of the view entry and expose only the classes this effect uses.
	const THREE = await import('./three-runtime');

	const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
	const hero = block || document.getElementById('hero');

	const PALETTE = {
		paper: getComputedStyle(document.documentElement).getPropertyValue('--wp--preset--color--grey').trim() || '#fff6ec',
		paperMesh: '#f0decb',
		key: '#FFFFFF',
		fill: '#ece2d7',
		rim: '#ffe6c9',
		amb: '#eecfae',
		warm: '#d4af87',
		warmShadow: [4.2, 0.9, 5.8]
	};

	const CFG = {
		segX: 12,
		segY: 8,
		planeW: 40,
		planeH: 30,

		amp: 5.4,
		detailAmp: 2.2,
		speed: 0.10,

		camDrift: 0.06,
		mouseInfluence: 0.95,
		meshParallax: 0.18,

		keyI: 1.58,
		fillI: 1.82,
		rimI: 8.08,
		ambI: 4.56,
		warmI: 8.9,

		fogDensity: 0.0565,
		fadeDist: () => window.innerHeight * 0.9,
		dprCap: 1.6,

		grainAmt: 0.035,
		grainPx: 1.5,
		grainSpeed: 0.01,

		multiply: 0.12,

		paperScale: 0.0626,
		paperStrength: 0.85,
		paperAniso: 0.54,

		cornerStrength: 0.03,
		cornerSize: 0.20,
		edgeStrength: 0.05,
		edgeSize: 0.5
	};

	const paper = new THREE.Color(PALETTE.paper);

	const scene = new THREE.Scene();
	scene.background = paper;
	scene.fog = new THREE.FogExp2(paper.getHex(), CFG.fogDensity);

	const renderer = new THREE.WebGLRenderer({
		antialias: true,
		alpha: false,
		premultipliedAlpha: true
	});

	renderer.setPixelRatio(Math.min(devicePixelRatio || 1, CFG.dprCap));
	renderer.setClearColor(paper, 1);
	renderer.outputColorSpace = THREE.SRGBColorSpace;
	renderer.toneMapping = THREE.ACESFilmicToneMapping;
	renderer.toneMappingExposure = 1.12;

	mount.appendChild(renderer.domElement);
	renderer.domElement.style.width = '100%';
	renderer.domElement.style.height = '100%';
	renderer.domElement.style.display = 'block';

	const camera = new THREE.PerspectiveCamera(34, 1, 0.5, 100);
	camera.position.set(0.18, 0.25, 6.4);

	const key = new THREE.DirectionalLight(new THREE.Color(PALETTE.key), CFG.keyI);
	key.position.set(3.6, 3.1, 2.4);
	scene.add(key);

	const fill = new THREE.DirectionalLight(new THREE.Color(PALETTE.fill), CFG.fillI);
	fill.position.set(-3.2, 1.7, 2.7);
	scene.add(fill);

	const rim = new THREE.DirectionalLight(new THREE.Color(PALETTE.rim), CFG.rimI);
	rim.position.set(0.2, -2.8, -2.8);
	scene.add(rim);

	const amb = new THREE.AmbientLight(new THREE.Color(PALETTE.amb), CFG.ambI);
	scene.add(amb);

	const warmLight = new THREE.DirectionalLight(new THREE.Color(PALETTE.warm), CFG.warmI);
	warmLight.position.set(4.2, 0.3, 1.0);
	scene.add(warmLight);

	function addBarycentricAttribute(geo) {
		const g = geo.index ? geo.toNonIndexed() : geo;
		const posAttr = g.attributes.position;
		const triCount = posAttr.count / 3;
		const bary = new Float32Array(posAttr.count * 3);

		for (let i = 0; i < triCount; i++) {
			const a = (i * 3 + 0) * 3;
			const b = (i * 3 + 1) * 3;
			const c = (i * 3 + 2) * 3;
			bary[a] = 1; bary[a + 1] = 0; bary[a + 2] = 0;
			bary[b] = 0; bary[b + 1] = 1; bary[b + 2] = 0;
			bary[c] = 0; bary[c + 1] = 0; bary[c + 2] = 1;
		}

		g.setAttribute('aBary', new THREE.BufferAttribute(bary, 3));
		return g;
	}

	let geo = new THREE.PlaneGeometry(CFG.planeW, CFG.planeH, CFG.segX, CFG.segY);
	geo = addBarycentricAttribute(geo);
	const pos = geo.attributes.position;

	for (let i = 0; i < pos.count; i++) {
		const x = pos.getX(i);
		const y = pos.getY(i);
		const nx = Math.sin(x * 0.32 + y * 0.12) * 0.5 + 0.5;
		const ny = Math.cos(y * 0.26 - x * 0.10) * 0.5 + 0.5;
		const scaleX = 1.0 + (nx - 0.5) * 0.55;
		const scaleY = 1.0 + (ny - 0.5) * 0.55;
		pos.setX(i, x * scaleX);
		pos.setY(i, y * scaleY);
	}
	pos.needsUpdate = true;

	const base = new Float32Array(pos.array.length);
	base.set(pos.array);

	const mat = new THREE.MeshStandardMaterial({
		color: new THREE.Color(PALETTE.paperMesh),
		roughness: 0.66,
		metalness: 0.0,
		flatShading: true
	});

	mat.onBeforeCompile = (shader) => {
		shader.uniforms.uTime = { value: 0.0 };
		shader.uniforms.uYMin = { value: -CFG.planeH * 0.5 };
		shader.uniforms.uYMax = { value: CFG.planeH * 0.5 };

		shader.uniforms.uWarmShadow = { value: new THREE.Vector3(...PALETTE.warmShadow) };

		shader.uniforms.uGrainPx = { value: CFG.grainPx };
		shader.uniforms.uGrainSpeed = { value: CFG.grainSpeed };
		shader.uniforms.uGrainAmt = { value: CFG.grainAmt };

		shader.uniforms.uPaperCol = { value: new THREE.Color(PALETTE.paper) };
		shader.uniforms.uMultiply = { value: CFG.multiply };

		shader.uniforms.uPaperScale = { value: CFG.paperScale };
		shader.uniforms.uPaperStrength = { value: CFG.paperStrength };
		shader.uniforms.uPaperAniso = { value: CFG.paperAniso };

		shader.uniforms.uCornerStrength = { value: CFG.cornerStrength };
		shader.uniforms.uCornerSize = { value: CFG.cornerSize };
		shader.uniforms.uEdgeStrength = { value: CFG.edgeStrength };
		shader.uniforms.uEdgeSize = { value: CFG.edgeSize };

		mat.userData.shader = shader;

		shader.vertexShader = shader.vertexShader
			.replace(
				`#include <common>`,
				`#include <common>
				 attribute vec3 aBary;
				 varying vec3 vBary;
				 varying float vH;
				 varying float vY;`
			)
			.replace(
				`#include <begin_vertex>`,
				`#include <begin_vertex>
				 vBary = aBary;
				 vH = transformed.z;
				 vY = transformed.y;`
			);

		shader.fragmentShader = shader.fragmentShader
			.replace(
				`#include <common>`,
				`#include <common>
				 varying vec3 vBary;
				 varying float vH;
				 varying float vY;

				 uniform float uTime;
				 uniform float uYMin;
				 uniform float uYMax;

				 uniform vec3  uWarmShadow;

				 uniform float uGrainPx;
				 uniform float uGrainSpeed;
				 uniform float uGrainAmt;

				 uniform vec3  uPaperCol;
				 uniform float uMultiply;

				 uniform float uPaperScale;
				 uniform float uPaperStrength;
				 uniform float uPaperAniso;

				 uniform float uCornerStrength;
				 uniform float uCornerSize;
				 uniform float uEdgeStrength;
				 uniform float uEdgeSize;

				 float secondMin3(float a, float b, float c){
					float mx = max(a, max(b,c));
					return min(max(a,b), min(mx,c));
				}`
			)
			.replace(
				`#include <dithering_fragment>`,
				`
				// Valley shading (softer)
				float depth = clamp(-vH * 0.22, 0.0, 1.0);
				depth = smoothstep(0.0, 1.0, depth);
				float shade = mix(1.03, 0.92, depth);
				gl_FragColor.rgb *= shade;

				// Warm the valleys gently
				gl_FragColor.rgb = mix(gl_FragColor.rgb, gl_FragColor.rgb * uWarmShadow, 0.005 + depth * 0.07);

				// Subtle vertical falloff
				float vNorm = clamp((vY - uYMin) / (uYMax - uYMin), 0.0, 1.0);
				float vShade = mix(0.995, 1.04, vNorm);
				gl_FragColor.rgb *= vShade;

				// Crease definition + mild glow
				float crease = clamp(fwidth(vH) * 11.0, 0.0, 1.0);
				gl_FragColor.rgb *= (1.0 - crease * 0.055);
				float glow = smoothstep(0.8, 0.85, crease);
				gl_FragColor.rgb += vec3(1.0) * glow * 0.055;

				// Soft corner/edge darkening (barycentric)
				float m1 = min(vBary.x, min(vBary.y, vBary.z));
				float edge = 1.0 - smoothstep(0.0, uEdgeSize, m1);
				float m2 = secondMin3(vBary.x, vBary.y, vBary.z);
				float c1 = 1.0 - smoothstep(0.0, uCornerSize, m1);
				float c2 = 1.0 - smoothstep(0.0, uCornerSize, m2);
				float corner = pow(c1 * c2, 1.1);
				float dark = corner * uCornerStrength + edge * uEdgeStrength;
				gl_FragColor.rgb *= (1.0 - dark);

				// Paper micro-texture
				vec2 p = gl_FragCoord.xy * uPaperScale;
				float a = sin(p.x * 120.0) * sin(p.y * (120.0 * (1.0 - uPaperAniso) + 60.0 * uPaperAniso));
				float b = sin(p.x * 240.0 + 1.7) * sin(p.y * 220.0 + 2.3);
				float weave = mix(a, b, 0.35);
				weave = weave * 0.5 + 0.5;
				float tex = mix(0.992, 1.008, weave);
				gl_FragColor.rgb *= mix(1.0, tex, uPaperStrength);

				// Embed into page colour
				vec3 multiplied = uPaperCol * gl_FragColor.rgb;
				gl_FragColor.rgb = mix(gl_FragColor.rgb, multiplied, uMultiply);

				// Grain (very light)
				vec2 grainUV = floor(gl_FragCoord.xy / uGrainPx);
				float g = fract(sin(dot(grainUV + uTime * uGrainSpeed, vec2(12.9898,78.233))) * 43758.5453);
				g = (g - 0.5) * uGrainAmt;
				gl_FragColor.rgb += g;

				#include <dithering_fragment>
				`
			);
	};

	const mesh = new THREE.Mesh(geo, mat);
	mesh.rotation.x = -0.48;
	mesh.rotation.y = 0.22;
	mesh.position.set(1.25, 0.35, -0.35);
	scene.add(mesh);

	function hash(x, y) {
		const v = Math.sin(x * 127.1 + y * 311.7) * 43758.5453123;
		return ((v % 1) + 1) % 1;
	}

	function smoothstep(t) {
		return t * t * (3 - 2 * t);
	}

	function noise2(x, y) {
		const ix = Math.floor(x);
		const iy = Math.floor(y);
		const fx = x - ix;
		const fy = y - iy;
		const a = hash(ix, iy);
		const b = hash(ix + 1, iy);
		const c = hash(ix, iy + 1);
		const d = hash(ix + 1, iy + 1);
		const ux = smoothstep(fx);
		const uy = smoothstep(fy);
		const ab = a + (b - a) * ux;
		const cd = c + (d - c) * ux;
		return ab + (cd - ab) * uy;
	}

	function fbm(x, y, t) {
		let v = 0;
		let a = 0.62;
		let f = 0.42;
		for (let i = 0; i < 2; i++) {
			v += a * noise2(x * f + t, y * f - t);
			f *= 2.05;
			a *= 0.55;
		}
		return v;
	}

	let targetMouseX = 0;
	let targetMouseY = 0;
	let mouseX = 0;
	let mouseY = 0;

	const mouseTarget = hero || block || window;
	mouseTarget.addEventListener('mousemove', (e) => {
		if (hero && hero !== window && typeof hero.getBoundingClientRect === 'function') {
			const r = hero.getBoundingClientRect();
			targetMouseX = ((e.clientX - r.left) / r.width) * 2 - 1;
			targetMouseY = ((e.clientY - r.top) / r.height) * 2 - 1;
			return;
		}

		targetMouseX = (e.clientX / window.innerWidth) * 2 - 1;
		targetMouseY = (e.clientY / window.innerHeight) * 2 - 1;
	}, { passive: true });

	// function onScroll() {
	// 	const a = Math.max(0, Math.min(1, 1 - (window.scrollY / CFG.fadeDist())));
	// 	mount.style.opacity = a.toFixed(3);
	// }
	// window.addEventListener('scroll', onScroll, { passive: true });
	// onScroll();

	function resize() {
		const rect = mount.getBoundingClientRect();
		const width = Math.max(1, Math.ceil(rect.width || window.innerWidth));
		const height = Math.max(1, Math.ceil(rect.height || window.innerHeight));
		renderer.setSize(width, height, false);
		renderer.setPixelRatio(Math.min(devicePixelRatio || 1, CFG.dprCap));
		camera.aspect = width / height;
		camera.updateProjectionMatrix();
	}

	const resizeObserver = new ResizeObserver(resize);
	resizeObserver.observe(mount);
	window.addEventListener('resize', resize, { passive: true });
	resize();

	const clock = new THREE.Clock();
	let running = true;

	function tick() {
		if (!running) {
			return;
		}

		const t = clock.getElapsedTime();

		mouseX += (targetMouseX - mouseX) * 0.045;
		mouseY += (targetMouseY - mouseY) * 0.045;

		camera.position.x = 0.18 + Math.sin(t * 0.12) * CFG.camDrift + mouseX * CFG.mouseInfluence;
		camera.position.y = 0.25 + Math.cos(t * 0.10) * (CFG.camDrift * 0.65) - mouseY * (CFG.mouseInfluence * 0.35);
		camera.position.z = 6.4 + Math.sin(t * 0.07) * 0.10;

		camera.lookAt(
			0.8 + mouseX * 0.25,
			0.0 - mouseY * 0.18,
			-0.45
		);

		mesh.position.x = 1.25 + mouseX * CFG.meshParallax * 0.30;
		mesh.position.y = 0.35 - mouseY * CFG.meshParallax * 0.16;

		key.position.x = 3.6 + mouseX * 0.9;
		key.position.y = 3.1 - mouseY * 0.6;
		warmLight.position.x = 4.2 + mouseX * 0.7;
		warmLight.position.y = 0.3 - mouseY * 0.4;

		const arr = pos.array;
		for (let i = 0; i < arr.length; i += 3) {
			const x = base[i];
			const y = base[i + 1];

			const n1 = fbm(x * 0.16 + 10.0, y * 0.16 + 10.0, t * CFG.speed);
			const n2 = fbm(x * 0.34 + 30.0, y * 0.34 + 30.0, t * (CFG.speed * 1.18));

			const m = (mouseX * 0.20 - mouseY * 0.14);
			arr[i + 2] = (n1 - 0.5) * CFG.amp + (n2 - 0.5) * CFG.detailAmp + m;
		}

		pos.needsUpdate = true;
		geo.computeVertexNormals();

		if (mat.userData.shader) {
			mat.userData.shader.uniforms.uTime.value = t;
		}

		renderer.render(scene, camera);
		if (!reduced) {
			requestAnimationFrame(tick);
		}
	}

	if (!reduced) {
		tick();
	} else {
		renderer.render(scene, camera);
	}

	document.addEventListener('visibilitychange', () => {
		if (document.hidden) {
			running = false;
		} else if (!running) {
			running = true;
			tick();
		}
	});
}

const HERO_BLUR_MOBILE_QUERY = '(max-width: 860px)';
const HERO_BLUR_REDUCED_QUERY = '(prefers-reduced-motion: reduce)';
const heroBlurPanelControllers = new WeakMap();

function destroyHeroBlurPanels(block) {
	const controller = heroBlurPanelControllers.get(block);
	if (controller) {
		controller.destroy();
	}
}

function clearHeroBlurPanelDesktopStyles(block) {
	if (!block) {
		return;
	}

	block.querySelectorAll('.fc-hero-blur__project-panel').forEach((panel) => {
		panel.style.transform = '';
	});
	block.querySelectorAll('.fc-hero-blur__project-media').forEach((media) => {
		media.style.transform = '';
	});
	block.querySelectorAll('.fc-hero-blur__project-spec').forEach((spec) => {
		spec.style.transform = '';
		spec.style.opacity = '';
	});
}

function refreshHeroBlurMobilePanels(block) {
	clearHeroBlurPanelDesktopStyles(block);
	requestAnimationFrame(() => {
		clearHeroBlurPanelDesktopStyles(block);
		requestAnimationFrame(() => {
			clearHeroBlurPanelDesktopStyles(block);
			window.dispatchEvent(new Event('resize'));
		});
	});
}

function initHeroBlurPanels(block) {
	if (!block || heroBlurPanelControllers.has(block)) {
		return;
	}

	const panels = Array.from(block.querySelectorAll('.fc-hero-blur__project-panel'));
	const collage = block.querySelector('.fc-hero-blur__projects-collage');
	if (!panels.length) {
		return;
	}

	const reduced = matchMedia(HERO_BLUR_REDUCED_QUERY).matches;
	const isMobile = matchMedia(HERO_BLUR_MOBILE_QUERY).matches;

	if (isMobile) {
		return;
	}

	if (reduced) {
		return;
	}

	const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

	const MAX_YAW = 8;
	const MAX_PITCH = 3;
	const FOLLOW = 0.08;
	const FRICTION = 0.9;
	const RETURN = 0.04;

	const REPEL_RADIUS = 900;
	const MAX_REPEL_PUSH = 130;
	const MAX_REPEL_SHRINK = 0.35;
	const PANEL_GAP = 34;
	const OVERLAP_CLEARANCE = 22;
	const ACTIVE_HOVER_SCALE = 1.05;
	const RESET_BEFORE_NEXT_HOVER_MS = 340;
	const REPEL_RAMP_MS = 2400;
	const REPEL_LERP_ACTIVE = 0.06;
	const REPEL_LERP_AWAY = 0.008;
	const REPEL_LERP_RETURN = 0.04;
	const REPEL_SETTLE_EPSILON = 0.1;

	const panelStates = panels.map((panel) => {
		const media = panel.querySelector('.fc-hero-blur__project-media');
		const spec = panel.querySelector('.fc-hero-blur__project-spec');
		const css = getComputedStyle(panel);

		return {
			panel,
			media,
			spec,
			baseX: css.getPropertyValue('--x').trim() || '0px',
			baseY: css.getPropertyValue('--y').trim() || '0px',
			baseScale: Number.parseFloat(css.getPropertyValue('--fc-panel-scale')) || 1,
			hoverShift: Number.parseFloat(css.getPropertyValue('--fc-panel-hover-shift')) || 0,
			lastX: 0,
			velocity: 0,
			currentVelocity: 0,
			yaw: 0,
			pitch: 0,
			isActive: false,
			raf: null,
			repelX: 0,
			repelY: 0,
			repelScale: 1,
			scrollOffset: 0,
			hoverScale: 1,
			hoverScaleTarget: 1
		};
	});
	const panelStateByElement = new Map(panelStates.map((state) => [state.panel, state]));
	const getCollageWidth = () => {
		const viewportWidth = Math.max(window.innerWidth || 0, document.documentElement.clientWidth || 0);
		const collageWidth = collage ? collage.getBoundingClientRect().width : 0;
		return Math.max(1, Math.min(collageWidth || viewportWidth, viewportWidth));
	};

	let activeState = null;
	let hoverResumeAt = 0;
	let pendingHover = null;
	let activeStartedAt = 0;
	let pointerX = 0;
	let pointerY = 0;
	let repulsionRaf = 0;
	let destroyed = false;
	const panelPointerEnterHandlers = [];
	const isPointerWithinPanel = (state, pointX, pointY, bleed = 10) => {
		if (!state) {
			return false;
		}

		const rect = state.panel.getBoundingClientRect();
		return (
			pointX >= rect.left - bleed &&
			pointX <= rect.right + bleed &&
			pointY >= rect.top - bleed &&
			pointY <= rect.bottom + bleed
		);
	};

	const clearPendingHover = () => {
		pendingHover = null;
	};

	const setActiveState = (nextState, pointerX = 0, pointerY = 0) => {
		if (destroyed) {
			return;
		}

		if (activeState === nextState) {
			return;
		}

		if (activeState) {
			const previousActiveState = activeState;
			previousActiveState.isActive = false;
			previousActiveState.hoverScaleTarget = 1;
			start(previousActiveState);
		}

		activeState = nextState;

		if (!activeState) {
			hoverResumeAt = performance.now() + RESET_BEFORE_NEXT_HOVER_MS;
			activeStartedAt = 0;
			clearPendingHover();
			updateRepulsion(0, 0);
			ensureRepulsionLoop();
			return;
		}

		activeState.isActive = true;
		activeStartedAt = performance.now();
		activeState.hoverScaleTarget = ACTIVE_HOVER_SCALE;
		activeState.lastX = pointerX;
		updateRepulsion(pointerX, pointerY);
		ensureRepulsionLoop();
		start(activeState);
	};

	const applyPanelState = (state) => {
		if (destroyed) {
			return;
		}

		const effectiveScale = state.baseScale * state.repelScale * state.hoverScale;
		state.panel.style.transform =
			`translate3d(calc(${state.baseX} - 50% + ${state.repelX}px), calc(${state.baseY} - 50% + ${state.repelY}px + ${state.scrollOffset}px), 0px) scale(${effectiveScale})`;

		if (state.media) {
			const mediaX = state.yaw * -2;
			const mediaY = state.pitch * -1.6;
			state.media.style.transform =
				`perspective(900px) translate3d(${mediaX}px, ${mediaY}px, 0) rotateX(${state.pitch}deg) rotateY(${state.yaw}deg)`;
		}

		if (state.spec) {
			state.spec.style.transform =
				`perspective(900px) translate3d(${state.yaw * 12}px, ${state.pitch * -12}px, 70px) rotateX(${state.pitch * 0.6}deg) rotateY(${state.yaw * 0.6}deg)`;
			state.spec.style.opacity = `${0.48 + Math.min(0.22, Math.abs(state.yaw) / 40)}`;
		}
	};

	const updateRepulsion = (pointerX, pointerY) => {
		if (destroyed) {
			return;
		}

		const activeRect = activeState ? activeState.panel.getBoundingClientRect() : null;
		const activeCenterX = activeRect ? (activeRect.left + activeRect.right) / 2 : 0;
		const activeCenterY = activeRect ? (activeRect.top + activeRect.bottom) / 2 : 0;
		const rawRamp = activeState ? clamp((performance.now() - activeStartedAt) / REPEL_RAMP_MS, 0, 1) : 1;
		const repelRamp = rawRamp * rawRamp * (3 - (2 * rawRamp));

		panelStates.forEach((state) => {
			let nextRepelX = 0;
			let nextRepelY = 0;
			let nextRepelScale = 1;

			if (state === activeState) {
				nextRepelX =
					state.panel.offsetWidth * state.baseScale * state.hoverShift;
				nextRepelY = 0;
				nextRepelScale = 1;
				state.hoverScaleTarget = ACTIVE_HOVER_SCALE;
				state.repelX += (nextRepelX - state.repelX) * REPEL_LERP_ACTIVE;
				state.repelY += (nextRepelY - state.repelY) * REPEL_LERP_ACTIVE;
				state.repelScale += (nextRepelScale - state.repelScale) * REPEL_LERP_ACTIVE;
				return;
			}

			if (!activeState) {
				nextRepelX = 0;
				nextRepelY = 0;
				nextRepelScale = 1;
				state.hoverScaleTarget = 1;
				state.repelX += (nextRepelX - state.repelX) * REPEL_LERP_RETURN;
				state.repelY += (nextRepelY - state.repelY) * REPEL_LERP_RETURN;
				state.repelScale += (nextRepelScale - state.repelScale) * REPEL_LERP_RETURN;
				applyPanelState(state);
				return;
			}

			const rect = state.panel.getBoundingClientRect();
			const centerX = rect.left + (rect.width / 2);
			const centerY = rect.top + (rect.height / 2);
			const dx = centerX - pointerX;
			const dy = centerY - pointerY;
			const distance = Math.hypot(dx, dy);
			const influence = clamp(1 - (distance / REPEL_RADIUS), 0, 1) * repelRamp;

			if (!influence) {
				nextRepelX = 0;
				nextRepelY = 0;
				nextRepelScale = 1;
			} else {
				const unitX = dx / (distance || 1);
				const unitY = dy / (distance || 1);
				nextRepelX = unitX * MAX_REPEL_PUSH * influence;
				nextRepelY = unitY * (MAX_REPEL_PUSH * 0.6) * influence;
				nextRepelScale = 1 - (MAX_REPEL_SHRINK * influence);

				// Keep non-active panels at a minimum width relative to the capped collage.
				const minPanelWidthPx = getCollageWidth() * 0.17;
				const basePanelWidthPx = Math.max(
					(state.panel.offsetWidth || rect.width) * state.baseScale,
					1
				);
				const minScaleForWidth = Math.min(1, minPanelWidthPx / basePanelWidthPx);
				nextRepelScale = Math.max(nextRepelScale, minScaleForWidth);
			}

			// Guarantee a minimum visual gap from the hovered card.
			if (activeRect) {
				const safeLeft = activeRect.left - PANEL_GAP;
				const safeRight = activeRect.right + PANEL_GAP;
				const safeTop = activeRect.top - PANEL_GAP;
				const safeBottom = activeRect.bottom + PANEL_GAP;

				// Iterate a few times so we fully clear overlap, not just nudge.
				for (let i = 0; i < 4; i++) {
					const shiftedLeft = rect.left + nextRepelX;
					const shiftedRight = rect.right + nextRepelX;
					const shiftedTop = rect.top + nextRepelY;
					const shiftedBottom = rect.bottom + nextRepelY;

					const overlapX = Math.min(safeRight, shiftedRight) - Math.max(safeLeft, shiftedLeft);
					const overlapY = Math.min(safeBottom, shiftedBottom) - Math.max(safeTop, shiftedTop);

					if (overlapX <= 0 || overlapY <= 0) {
						break;
					}

					let dirX = shiftedLeft + ((shiftedRight - shiftedLeft) / 2) >= activeCenterX ? 1 : -1;
					let dirY = shiftedTop + ((shiftedBottom - shiftedTop) / 2) >= activeCenterY ? 1 : -1;

					if (!Number.isFinite(dirX) || dirX === 0) {
						dirX = centerX >= pointerX ? 1 : -1;
					}
					if (!Number.isFinite(dirY) || dirY === 0) {
						dirY = centerY >= pointerY ? 1 : -1;
					}

					const pushX = dirX * (overlapX + OVERLAP_CLEARANCE);
					const pushY = dirY * (overlapY + OVERLAP_CLEARANCE);

					if (Math.abs(pushX) <= Math.abs(pushY)) {
						nextRepelX += pushX;
					} else {
						nextRepelY += pushY;
					}
				}
			}

			state.repelX += (nextRepelX - state.repelX) * REPEL_LERP_AWAY;
			state.repelY += (nextRepelY - state.repelY) * REPEL_LERP_AWAY;
			state.repelScale += (nextRepelScale - state.repelScale) * REPEL_LERP_AWAY;

			applyPanelState(state);
		});
	};

	const hasRepulsionWork = () => {
		if (activeState) {
			return true;
		}

		return panelStates.some((state) =>
			Math.abs(state.repelX) > REPEL_SETTLE_EPSILON ||
			Math.abs(state.repelY) > REPEL_SETTLE_EPSILON ||
			Math.abs(1 - state.repelScale) > 0.001
		);
	};

	const settleRepulsion = () => {
		panelStates.forEach((state) => {
			state.repelX = 0;
			state.repelY = 0;
			state.repelScale = 1;
			applyPanelState(state);
		});
	};

	const runRepulsionLoop = () => {
		if (destroyed) {
			repulsionRaf = 0;
			return;
		}

		updateRepulsion(pointerX, pointerY);

		if (hasRepulsionWork()) {
			repulsionRaf = requestAnimationFrame(runRepulsionLoop);
			return;
		}

		repulsionRaf = 0;
		settleRepulsion();
	};

	function ensureRepulsionLoop() {
		if (repulsionRaf) {
			return;
		}

		repulsionRaf = requestAnimationFrame(runRepulsionLoop);
	}

	const start = (state) => {
		if (destroyed || state.raf) {
			return;
		}

		const tick = () => {
			if (destroyed) {
				state.raf = null;
				return;
			}

			state.currentVelocity += (state.velocity - state.currentVelocity) * FOLLOW;
			state.velocity *= FRICTION;
			state.hoverScale += (state.hoverScaleTarget - state.hoverScale) * 0.08;

			const targetYaw = clamp(state.currentVelocity * 0.18, -MAX_YAW, MAX_YAW);
			state.yaw += (targetYaw - state.yaw) * 0.12;

			const targetPitch = clamp(-Math.abs(state.yaw) * 0.3, -MAX_PITCH, 0);
			state.pitch += (targetPitch - state.pitch) * 0.1;

			if (!state.isActive) {
				state.yaw += (0 - state.yaw) * RETURN;
				state.pitch += (0 - state.pitch) * RETURN;
			}

			applyPanelState(state);

			if (!state.isActive && Math.abs(state.yaw) < 0.02 && Math.abs(state.currentVelocity) < 0.02) {
				state.yaw = 0;
				state.pitch = 0;
				state.currentVelocity = 0;
				applyPanelState(state);
				state.raf = null;
				return;
			}

			state.raf = requestAnimationFrame(tick);
		};

		state.raf = requestAnimationFrame(tick);
	};

	panelStates.forEach((state) => {
		const onPointerEnter = (event) => {
			setActiveState(state, event.clientX, event.clientY);
		};
		state.panel.addEventListener('pointerenter', onPointerEnter);
		panelPointerEnterHandlers.push([state.panel, onPointerEnter]);

		applyPanelState(state);
	});

	const parallaxDirections = [1, -1, 1, -1, 1];
	let scrollRaf = 0;
	const updateScrollParallax = () => {
		if (destroyed) {
			return;
		}

		const rect = block.getBoundingClientRect();
		const viewportH = window.innerHeight || 1;
		const total = rect.height + viewportH;
		const progress = clamp((viewportH - rect.top) / total, 0, 1);
		const baseOffset = (progress - 0.5) * 2 * 42;
		panelStates.forEach((state, idx) => {
			const dir = parallaxDirections[idx] || 1;
			state.scrollOffset = baseOffset * dir;
			applyPanelState(state);
		});
	};
	const onScroll = () => {
		if (scrollRaf) return;
		scrollRaf = requestAnimationFrame(() => {
			scrollRaf = 0;
			if (destroyed) {
				return;
			}
			updateScrollParallax();
		});
	};
	window.addEventListener('scroll', onScroll, { passive: true });
	window.addEventListener('resize', updateScrollParallax);
	updateScrollParallax();

	const onPointerMove = (event) => {
		if (destroyed) {
			return;
		}

		if (!(event.target instanceof Element) || !block.contains(event.target)) {
			pointerX = 0;
			pointerY = 0;
			setActiveState(null);
			return;
		}

		pointerX = event.clientX;
		pointerY = event.clientY;
		ensureRepulsionLoop();

		const hoveredPanel = event.target.closest('.fc-hero-blur__project-panel');
		const hoveredState = hoveredPanel ? panelStateByElement.get(hoveredPanel) || null : null;
		const now = performance.now();

		if (now < hoverResumeAt) {
			if (hoveredState) {
				pendingHover = {
					state: hoveredState,
					x: event.clientX,
					y: event.clientY
				};
			}
			return;
		}

		if (!activeState && pendingHover) {
			setActiveState(pendingHover.state, pendingHover.x, pendingHover.y);
			clearPendingHover();
		}

		if (!hoveredState) {
			// Briefly tolerate hit-test misses while still on the active card.
			if (activeState && isPointerWithinPanel(activeState, event.clientX, event.clientY)) {
				const deltaX = event.clientX - activeState.lastX;
				activeState.lastX = event.clientX;
				activeState.velocity = clamp(deltaX, -60, 60);
				updateRepulsion(event.clientX, event.clientY);
				start(activeState);
				return;
			}

			updateRepulsion(event.clientX, event.clientY);
			setActiveState(null);
			return;
		}

		if (activeState && hoveredState !== activeState) {
			pendingHover = {
				state: hoveredState,
				x: event.clientX,
				y: event.clientY
			};
			updateRepulsion(event.clientX, event.clientY);
			setActiveState(null);
			return;
		}

		setActiveState(hoveredState, event.clientX, event.clientY);

		const deltaX = event.clientX - hoveredState.lastX;
		hoveredState.lastX = event.clientX;
		hoveredState.velocity = clamp(deltaX, -60, 60);
		updateRepulsion(event.clientX, event.clientY);
		start(hoveredState);
	};
	window.addEventListener('pointermove', onPointerMove, { passive: true });

	const onPointerLeave = () => {
		if (destroyed) {
			return;
		}

		pointerX = 0;
		pointerY = 0;
		updateRepulsion(0, 0);
		setActiveState(null);
	};
	block.addEventListener('pointerleave', onPointerLeave);

	heroBlurPanelControllers.set(block, {
		destroy() {
			if (destroyed) {
				return;
			}

			destroyed = true;
			activeState = null;
			clearPendingHover();

			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('resize', updateScrollParallax);
			window.removeEventListener('pointermove', onPointerMove);
			block.removeEventListener('pointerleave', onPointerLeave);

			panelPointerEnterHandlers.forEach(([panel, handler]) => {
				panel.removeEventListener('pointerenter', handler);
			});

			if (scrollRaf) {
				cancelAnimationFrame(scrollRaf);
				scrollRaf = 0;
			}
			if (repulsionRaf) {
				cancelAnimationFrame(repulsionRaf);
				repulsionRaf = 0;
			}

			panelStates.forEach((state) => {
				if (state.raf) {
					cancelAnimationFrame(state.raf);
					state.raf = null;
				}
			});

			clearHeroBlurPanelDesktopStyles(block);
			heroBlurPanelControllers.delete(block);
		}
	});
}

document.addEventListener('DOMContentLoaded', () => {
	// Guard against running ThreeJS or canvas calculations inside the CMS block editor
	if (
		(document.body && document.body.classList.contains('wp-admin')) ||
		typeof window.wp?.blockEditor !== 'undefined' ||
		document.querySelector('.editor-styles-wrapper')
	) {
		return;
	}

	const globalMount = document.querySelector('.fc-global-blur-bg__canvas');
	const canRunDecorativeBlur = !window.matchMedia(
		`${HERO_BLUR_MOBILE_QUERY}, ${HERO_BLUR_REDUCED_QUERY}`
	).matches;
	if (globalMount && canRunDecorativeBlur) {
		// The WebGL background is above-the-fold, so initialise it as soon as the
		// page has loaded instead of visibly starting a few seconds late.
		const startGlobalBlur = () => initHeroBlur(null, globalMount);
		const scheduleGlobalBlur = () => startGlobalBlur();

		if (document.readyState === 'complete') {
			scheduleGlobalBlur();
		} else {
			window.addEventListener('load', scheduleGlobalBlur, { once: true });
		}
	}

	const heroBlurBlocks = Array.from(document.querySelectorAll('.wp-block-fc-block-fc-hero-blur'));
	const heroBlurMobileQuery = window.matchMedia(HERO_BLUR_MOBILE_QUERY);
	const syncHeroBlurPanels = () => {
		heroBlurBlocks.forEach((block) => {
			if (heroBlurMobileQuery.matches) {
				destroyHeroBlurPanels(block);
				refreshHeroBlurMobilePanels(block);
				return;
			}

			initHeroBlurPanels(block);
		});
	};

	syncHeroBlurPanels();

	if (typeof heroBlurMobileQuery.addEventListener === 'function') {
		heroBlurMobileQuery.addEventListener('change', syncHeroBlurPanels);
	} else if (typeof heroBlurMobileQuery.addListener === 'function') {
		heroBlurMobileQuery.addListener(syncHeroBlurPanels);
	}
});
